import { IndividualLight } from "@/Common/Api/Individuals/Dto/IndividualLight";
import { ResponsibleProviderLight } from "@/Common/Api/Individuals/Dto/ResponsibleProviderLight";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { AttachedCommunication } from "@/Common/Models/AttachedCommunication/AttachedCommunication";
import { MyleObjectType } from "@/Common/Models/Enum/MyleObjectType";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { IPortalShareableItem } from "@/Common/Models/Portal/UI/IPortalShareableItem";
import { DocumentAnnotationLog } from "@/Documents/Api/Dto/Response/DocumentAnnotationLog";
import { DocumentNote } from "@/Documents/Api/Dto/Response/DocumentNote";
import { DocumentPath } from "@/Documents/Api/Dto/Response/DocumentPath";
import { PrescriptionRenewalItemResponse } from "@/PrescriptionRenewals/Api/Dto/PrescriptionRenewalItemResponse";
import { DocumentStatusEnum } from "./DocumentStatusEnum";
import { IDocumentActionHistory } from "./IDocumentActionHistory";

class MedicalDocumentItem implements IPortalShareableItem {
    public Model: MedicalDocumentItemDto;

    constructor(model?: MedicalDocumentItemDto) {
        this.Model = model;
    }

    public getMyleObjectType(): MyleObjectType {
        return MyleObjectType.Document;
    }

    public getGlobalPortalStatus(): PortalSyncStatus {
        return PortalHelper.getGlobalPortalStatus(this.getSharedItem(), this.getSharedItemMessages());
    }

    public getSharedItemId(): string {
        let sharedItem = this.getSharedItem();
        if (sharedItem && sharedItem.SharedItemId)
            return sharedItem.SharedItemId;

        return null;
    }

    public getSharedItem(): SharedItem {
        if (this.Model)
            return this.Model.SharedItem;

        return null;
    }

    public getSharedItemMessages(): SharedItemMessage[] {
        if (this.Model)
            return this.Model.SharedItemMessages;

        return [];
    }

    public getItemTypeHeader(): string {
        return cacheStore.GetLocalizedValueByLookupId(this.Model.ClassId) + "-" + cacheStore.GetLocalizedValueByLookupId(this.Model.SubClassId);
    }

    public getItemTitle(): string {
        return this.Model.Subject
            ?.truncate(SharedItem.SubjectMaxLength);
    }

    public getDefaultSubject(): string {
        return this.getItemTitle();
    }

    public getPortalObjectId(): string {
        return this.Model.Id;
    }

    public getPortalLinkedItemType(): PortalLinkedItemType {
        return PortalLinkedItemType.Document;
    }

    public getSharedItemPortalStatus(): PortalSyncStatus {
        return PortalSyncStatus.Unknown;
    }

    public hasBeenModifiedSinceLastShared(): boolean {
        return (
            this.Model.LastAnnotationDate
            && this.Model.SharedItem?.DateModified
            && new Date(this.Model.SharedItem.DateModified) < new Date(this.Model.LastAnnotationDate)
        );
    }

    public hasBeenSharedOnPreviousEncounter(): boolean {
        return false;
    }
}

class MedicalDocumentItemDto {
    public Id: string;
    public UploadDate: Date;
    public ReceiptDate: Date;
    public SignOffDate: Date;
    public UploaderContactId: string;
    public IsUrgent: boolean = false;
    public IsDraft: boolean = false;
    public ClassId: string;
    public SubClassId: string;
    public Subject: string;
    public Patients: IndividualLight[];
    public SignOffByContactId: string;
    public SiteId: string;
    public HRMStatusLookupId: string = "";
    public Source: string;
    public ResponsibleProviders: ResponsibleProviderLight[];
    public NoteInfoCollection: DocumentNote[];
    public SenderFaxNumber?: string;
    public InboundFaxId?: string;
    public SenderFaxSource?: string;
    public DocumentAnnotationsLog: DocumentAnnotationLog[];
    public RequiresSingleSignOffForApproval: boolean;

    public TotalRows: number;
    public DocumentPathList: DocumentPath[];

    public ObservationDateTime: Date;
    public AuthoringDateTime: Date;
    public SendingFacilityID: string;
    public SendingFacilityReport: string;
    public AccompanyingSubClass: string;
    public AccompanyingMnemonic: string;
    public LastModifiedById: string;
    public LastModifiedByDate: Date;
    public LastAnnotationDate: Date;
    public SourceAuthorName?: string;

    public SharedItemMessages: SharedItemMessage[];
    public SharedItem: SharedItem = new SharedItem();

    public RevokedByContactId: string;
    public RevokedOn: Date;

    public IndividualTaskLinkedItems: AttachedCommunication[];
    public AreAllTasksCompleted: boolean;
    public AreAllMessagesRead: boolean;
    public AreAllRecallsCompleted: boolean;
    public IsAnnotated: boolean;

    public PrescriptionRenewal: PrescriptionRenewalItemResponse;

    public DocumentActionHistory: IDocumentActionHistory[];
    public DocumentStatus: DocumentStatusEnum;
    public DocumentStatusVocabularyKey: string;
    public CanBeApprovedByCurrentUser: boolean;
}

export { MedicalDocumentItem, MedicalDocumentItemDto };
